import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useCompany } from '../contexts/CompanyContext';
import { terminalColors } from '../utils/terminalColors';
import { kpiAPI } from '../services/api';
import LandingPageKpiDashboard from '../components/LandingPageKpiDashboard';

// Helper function for color-coded cells - simplified to single colors
const getHeatmapColor = (color: string, value: number, target: number) => {
  if (color === 'green') {
    return '#A5D6A7'; // Single medium green
  } else if (color === 'yellow') {
    return '#FFF9C4'; // Single cream/light yellow
  } else if (color === 'red') {
    return '#EF9A9A'; // Single medium red
  }
  return '#ffffff';
};

// Editable cell component with color-coded background
const EditableCell: React.FC<{
  kpiId: number;
  period: string;
  entry: any;
  target: number;
  onSave: (kpiId: number, period: string, value: string, notes: string) => void;
  auditLinks?: any[];
  showAuditMarkers?: boolean;
  onViewAuditDetails?: (kpiId: number, kpiName: string, period?: string) => void;
  kpiName?: string;
}> = ({ kpiId, period, entry, target, onSave, auditLinks = [], showAuditMarkers = false, onViewAuditDetails, kpiName = '' }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(entry?.actual_value || '');
  const [notes, setNotes] = useState(entry?.notes || '');

  const handleSave = () => {
    // Only save if value is not empty
    if (value && value.trim() !== '') {
      onSave(kpiId, period, value, notes);
      setEditing(false);
    } else {
      // Reset to original value if empty
      setValue(entry?.actual_value || '');
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <TableCell sx={{ p: 0.5, minWidth: 80, maxWidth: 80, px: 0.5 }}>
        <TextField
          size="small"
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleSave}
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleSave();
          }}
          autoFocus
          sx={{ width: '100%', '& input': { fontSize: '0.875rem', p: 0.5 } }}
        />
      </TableCell>
    );
  }

  // Check if any audit started in this period
  const periodDate = new Date(period);
  const auditsInPeriod = showAuditMarkers ? auditLinks.filter((audit: any) => {
    const auditDate = new Date(audit.audit_date);
    return auditDate.getFullYear() === periodDate.getFullYear() &&
           auditDate.getMonth() === periodDate.getMonth();
  }) : [];
  
  const hasAuditInPeriod = auditsInPeriod.length > 0;

  return (
    <TableCell
      sx={{
        cursor: 'pointer',
        '&:hover': { opacity: 0.8 },
        minWidth: 80,
        maxWidth: 80,
        textAlign: 'center',
        px: 0.5,
        bgcolor: entry ? getHeatmapColor(entry.status_color, parseFloat(entry.actual_value), target) : '#ffffff',
        transition: 'opacity 0.2s',
        position: 'relative',
      }}
      onClick={() => setEditing(true)}
    >
      <Box sx={{ position: 'relative' }}>
        {/* Audit marker icon */}
        {hasAuditInPeriod && (
          <Tooltip title={`${auditsInPeriod.length} audit(s) completed this month`} arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (onViewAuditDetails) {
                  onViewAuditDetails(kpiId, kpiName, period);
                }
              }}
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                p: 0.25,
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                width: 20,
                height: 20,
                zIndex: 10,
              }}
            >
              <AssignmentIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        
        {entry ? (
          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.875rem' }}>
            {entry.actual_value}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </Box>
    </TableCell>
  );
};


/**
 * Convert grid data from API to Landing Page KPI Dashboard format
 */
const convertToLandingPageFormat = (gridData: any) => {
  if (!gridData || !gridData.kpis || !gridData.periods) return [];

  return gridData.kpis.map((kpiData: any) => {
    const kpi = kpiData.kpi;
    
    // Map category based on KPI metadata or default to 'Operations'
    const category = kpi.category || 'Operations';
    
    // Determine unit type
    let unit = '%'; // default
    if (kpi.unit_type === 'currency') unit = '$';
    else if (kpi.unit_type === 'days') unit = 'days';
    else if (kpi.unit_type === 'hours') unit = 'hours';
    else if (kpi.unit_type === 'percentage') unit = '%';
    else if (kpi.unit_type === 'number' || kpi.unit_type === 'count') unit = '';
    
    // Convert entries to data points
    const dataPoints = gridData.periods.map((period: string) => {
      const entry = kpiData.entries[period];
      const actualValue = entry ? parseFloat(entry.actual_value) : 0;
      
      return {
        date: period,
        value: actualValue || 0,
        target: parseFloat(kpi.target_value) || 0,
      };
    });

    return {
      id: kpi.id.toString(),
      name: kpi.name,
      category: category,
      unit: unit,
      dataPoints: dataPoints,
      auditLinks: kpiData.audit_links || [],
      hasAuditLinks: kpiData.has_audit_links || false,
    };
  });
};

const KPIDashboard: React.FC = () => {
  const { selectedCompany } = useCompany();
  const navigate = useNavigate();
  const { companyId: companyIdParam } = useParams<{ companyId: string }>();

  const [loading, setLoading] = useState(true);
  const [gridData, setGridData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [editingKpi, setEditingKpi] = useState<any>(null);
  const [generateConfirmOpen, setGenerateConfirmOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [allKpis, setAllKpis] = useState<any[]>([]);
  
  // Audit markers state
  const [showAuditMarkers, setShowAuditMarkers] = useState(() => {
    const saved = localStorage.getItem('kpi_show_audit_markers');
    return saved ? JSON.parse(saved) : true;
  });
  const [auditDetailsDialogOpen, setAuditDetailsDialogOpen] = useState(false);
  const [selectedKpiAudits, setSelectedKpiAudits] = useState<any>(null);
  const [loadingAuditDetails, setLoadingAuditDetails] = useState(false);
  
  // KPI form state
  const [kpiForm, setKpiForm] = useState<{
    name: string;
    description: string;
    unit_type: 'percentage' | 'currency' | 'number' | 'days' | 'count';
    target_value: string;
    tolerance_percentage: string;
    higher_is_better: boolean;
  }>({
    name: '',
    description: '',
    unit_type: 'number',
    target_value: '',
    tolerance_percentage: '10',
    higher_is_better: true,
  });

  // Use URL param as source of truth for companyId
  const companyId = companyIdParam ? parseInt(companyIdParam, 10) : selectedCompany?.id;

  useEffect(() => {
    if (companyId) {
      loadKPIData();
    }
  }, [companyId]);

  const loadKPIData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log(terminalColors.info(`📊 Loading KPI data for company ${companyId}`));
      console.log(terminalColors.info(`🔍 Selected company:`), selectedCompany);

      const data = await kpiAPI.getKPIGrid(companyId!);
      setGridData(data);

      console.log(terminalColors.success(`✅ Loaded ${data.kpis?.length || 0} KPIs`));
    } catch (err: any) {
      console.error(terminalColors.error(`❌ Failed to load KPI data: ${err.message}`));
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEntry = async (kpiId: number, period: string, actualValue: string, notes: string) => {
    try {
      // Validate that actualValue is not empty
      if (!actualValue || actualValue.trim() === '') {
        console.error(terminalColors.error('❌ Cannot save empty KPI value'));
        setError('Please enter a value for the KPI');
        return;
      }

      console.log(terminalColors.info(`💾 Saving KPI entry for KPI ${kpiId}, period ${period}`));

      const response = await kpiAPI.createKPIEntry({
        kpi_id: kpiId,
        period_date: period,
        actual_value: actualValue,
        notes: notes,
      });

      console.log(terminalColors.success('✅ KPI entry saved'));

      // Update grid data locally without full reload
      setGridData((prevData: any) => {
        if (!prevData) return prevData;
        
        const newData = { ...prevData };
        const kpiIndex = newData.kpis.findIndex((k: any) => k.kpi.id === kpiId);
        
        if (kpiIndex !== -1) {
          const newKpis = [...newData.kpis];
          newKpis[kpiIndex] = {
            ...newKpis[kpiIndex],
            entries: {
              ...newKpis[kpiIndex].entries,
              [period]: {
                actual_value: actualValue,
                notes: notes,
                status_color: response.entry?.status_color || 'grey',
                period_date: period,
              }
            }
          };
          newData.kpis = newKpis;
        }
        
        return newData;
      });
    } catch (err: any) {
      console.error(terminalColors.error(`❌ Failed to save KPI entry: ${err.message}`));
      setError(err.message);
    }
  };

  const handleExport = async () => {
    try {
      console.log(terminalColors.info('📥 Exporting KPI data to CSV'));
      await kpiAPI.exportKPIsCSV(companyId!);
      console.log(terminalColors.success('✅ KPI data exported'));
    } catch (err: any) {
      console.error(terminalColors.error(`❌ Export failed: ${err.message}`));
      setError(err.message);
    }
  };

  const handleManageKPIs = async () => {
    // Load all KPIs and open management dialog
    try {
      console.log(terminalColors.info(`📋 Loading all KPIs for management`));
      const data = await kpiAPI.getCompanyKPIs(companyId!);
      setAllKpis(data.kpis || []);
      setManageDialogOpen(true);
    } catch (err: any) {
      console.error(terminalColors.error(`❌ Failed to load KPIs: ${err.message}`));
      setError(err.message);
    }
  };

  const handleAddNewKPI = () => {
    // Close manage dialog, open create dialog
    setManageDialogOpen(false);
    setEditingKpi(null);
    setKpiForm({
      name: '',
      description: '',
      unit_type: 'number',
      target_value: '',
      tolerance_percentage: '10',
      higher_is_better: true,
    });
    setKpiDialogOpen(true);
  };

  const handleEditKpi = (kpi: any) => {
    setManageDialogOpen(false);
    setEditingKpi(kpi);
    setKpiForm({
      name: kpi.name,
      description: kpi.description || '',
      unit_type: kpi.unit_type,
      target_value: kpi.target_value,
      tolerance_percentage: kpi.tolerance_percentage,
      higher_is_better: kpi.higher_is_better,
    });
    setKpiDialogOpen(true);
  };

  const handleSaveKpi = async () => {
    try {
      console.log(terminalColors.info(`💾 Saving KPI: ${kpiForm.name}`));
      
      if (editingKpi) {
        // Update existing KPI
        await kpiAPI.updateKPI(editingKpi.id, kpiForm);
        console.log(terminalColors.success('✅ KPI updated'));
      } else {
        // Create new KPI
        await kpiAPI.createKPI(companyId!, kpiForm);
        console.log(terminalColors.success('✅ KPI created'));
      }
      
      setKpiDialogOpen(false);
      setEditingKpi(null);
      
      // Reload KPI data
      await loadKPIData();
      
      // If manage dialog was open, refresh its list too
      if (manageDialogOpen) {
        const data = await kpiAPI.getCompanyKPIs(companyId!);
        setAllKpis(data.kpis || []);
        setManageDialogOpen(true);
      }
    } catch (err: any) {
      console.error(terminalColors.error(`❌ Failed to save KPI: ${err.message}`));
      setError(err.message);
    }
  };

  const handleDeleteKpi = async (kpiId: number, kpiName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${kpiName}"? This will also delete all historical data for this KPI.`)) {
      return;
    }
    
    try {
      console.log(terminalColors.info(`🗑️ Deleting KPI ${kpiId}`));
      await kpiAPI.deleteKPI(kpiId);
      console.log(terminalColors.success('✅ KPI deleted'));
      
      // Reload KPI data
      await loadKPIData();
      
      // Update manage dialog list
      const data = await kpiAPI.getCompanyKPIs(companyId!);
      setAllKpis(data.kpis || []);
    } catch (err: any) {
      console.error(terminalColors.error(`❌ Failed to delete KPI: ${err.message}`));
      setError(err.message);
    }
  };

  const handleGenerateKPIs = () => {
    // Open confirmation dialog
    setGenerateConfirmOpen(true);
  };

  const handleToggleAuditMarkers = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setShowAuditMarkers(newValue);
    localStorage.setItem('kpi_show_audit_markers', JSON.stringify(newValue));
  };

  const handleViewAuditDetails = async (kpiId: number, kpiName: string, period?: string) => {
    try {
      setLoadingAuditDetails(true);
      
      if (period) {
        console.log(terminalColors.info(`📋 Loading audit details for KPI ${kpiId} in period ${period}`));
      } else {
        console.log(terminalColors.info(`📋 Loading all audit details for KPI ${kpiId}`));
      }
      
      const response = await kpiAPI.getKPIAuditDetails(kpiId);
      
      // Filter audits by period if provided
      let filteredAudits = response.audits || [];
      if (period) {
        const periodDate = new Date(period);
        filteredAudits = filteredAudits.filter((audit: any) => {
          const auditDate = new Date(audit.audit_date);
          return auditDate.getFullYear() === periodDate.getFullYear() &&
                 auditDate.getMonth() === periodDate.getMonth();
        });
        console.log(terminalColors.success(`✅ Filtered to ${filteredAudits.length} audit(s) in ${period}`));
      } else {
        console.log(terminalColors.success(`✅ Loaded ${filteredAudits.length} total audits`));
      }
      
      setSelectedKpiAudits({
        kpi: response.kpi,
        audits: filteredAudits,
        total: response.total_audits || 0,
        period: period || null // Track if we're showing filtered view
      });
      setAuditDetailsDialogOpen(true);
    } catch (err: any) {
      console.error(terminalColors.error(`❌ Failed to load audit details: ${err.message}`));
      setError(err.message);
    } finally {
      setLoadingAuditDetails(false);
    }
  };

  const handleGenerateKPIsConfirm = async () => {
    setGenerateConfirmOpen(false);
    
    try {
      setIsGenerating(true);
      console.log(terminalColors.info(`🎯 Triggering KPI generation for company ${companyId}`));
      
      const response = await kpiAPI.generateKPIsForCompany(companyId!);
      
      console.log(terminalColors.success('✅ KPI generation task started:'), response);
      
      // Show generating message
      // The background task will take a few seconds to complete
      // User can refresh or wait for auto-reload
      
      // Poll for completion (check every 5 seconds for up to 2 minutes)
      // LLM-based KPI generation can take 60-90 seconds with API timeout at 90s
      let attempts = 0;
      const maxAttempts = 24;
      const pollInterval = 5000; // 5 seconds
      
      const checkForKPIs = async () => {
        attempts++;
        console.log(terminalColors.info(`🔄 Checking for KPIs (attempt ${attempts}/${maxAttempts})`));
        
        const data = await kpiAPI.getKPIGrid(companyId!);
        
        if (data.kpis && data.kpis.length > 0) {
          // KPIs have been generated!
          console.log(terminalColors.success(`✅ KPIs generated! Found ${data.kpis.length} KPIs`));
          setIsGenerating(false);
          setGridData(data);
          setLoading(false);
          return true;
        }
        
        if (attempts < maxAttempts) {
          // Keep polling
          setTimeout(checkForKPIs, pollInterval);
          return false;
        } else {
          // Max attempts reached
          console.log(terminalColors.info('⏱️ Still generating... Please refresh the page in a moment'));
          setIsGenerating(false);
          setLoading(false);
          return false;
        }
      };
      
      // Start polling after initial delay
      setTimeout(checkForKPIs, pollInterval);
      
    } catch (err: any) {
      console.error(terminalColors.error(`❌ Failed to start KPI generation: ${err.message}`));
      setError(err.message);
      setIsGenerating(false);
      setLoading(false);
    }
  };

  if (!companyId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please select a company to view KPIs</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!gridData || gridData.kpis.length === 0) {
    return (
      <>
        <Box sx={{ p: 3 }}>
          {/* Generating Status Banner */}
          {isGenerating && (
            <Alert 
              severity="info" 
              sx={{ mb: 3 }}
              icon={<CircularProgress size={20} />}
            >
              <Typography variant="body1" fontWeight="medium">
                🤖 Generating KPIs with AI...
              </Typography>
              <Typography variant="body2">
                Our AI is analyzing your company data to create 10 industry-specific KPIs. 
                This usually takes 10-20 seconds. The page will automatically update when complete.
              </Typography>
            </Alert>
          )}

          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                No KPIs Configured
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                KPIs help track your company's performance over time. You can generate them automatically based on 
                industry best practices and company research, or create custom KPIs manually.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button 
                  variant="contained" 
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleGenerateKPIs}
                  disabled={loading || isGenerating}
                >
                  {isGenerating ? 'Generating...' : 'Auto-Generate KPIs'}
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<AddIcon />}
                  onClick={handleManageKPIs}
                  disabled={loading || isGenerating}
                >
                  Create Manually
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* KPI Create/Edit Dialog */}
        <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingKpi ? 'Edit KPI' : 'Create New KPI'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="KPI Name"
                value={kpiForm.name}
                onChange={(e) => setKpiForm({ ...kpiForm, name: e.target.value })}
                fullWidth
                required
                placeholder="e.g., On-Time Delivery Rate"
              />
              
              <TextField
                label="Description"
                value={kpiForm.description}
                onChange={(e) => setKpiForm({ ...kpiForm, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                placeholder="What does this KPI measure?"
              />
              
              <FormControl fullWidth>
                <InputLabel>Unit Type</InputLabel>
                <Select
                  value={kpiForm.unit_type}
                  onChange={(e) => setKpiForm({ ...kpiForm, unit_type: e.target.value })}
                  label="Unit Type"
                >
                  <MenuItem value="percentage">Percentage (%)</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="currency">Currency ($)</MenuItem>
                  <MenuItem value="days">Days</MenuItem>
                  <MenuItem value="count">Count</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                label="Target Value"
                value={kpiForm.target_value}
                onChange={(e) => setKpiForm({ ...kpiForm, target_value: e.target.value })}
                fullWidth
                required
                type="number"
                placeholder="e.g., 95"
              />
              
              <TextField
                label="Tolerance (%)"
                value={kpiForm.tolerance_percentage}
                onChange={(e) => setKpiForm({ ...kpiForm, tolerance_percentage: e.target.value })}
                fullWidth
                required
                type="number"
                placeholder="e.g., 10"
                helperText="Acceptable variance from target"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={kpiForm.higher_is_better}
                    onChange={(e) => setKpiForm({ ...kpiForm, higher_is_better: e.target.checked })}
                  />
                }
                label="Higher is Better"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setKpiDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveKpi} 
              variant="contained" 
              disabled={!kpiForm.name || !kpiForm.target_value}
            >
              {editingKpi ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Generate KPIs Confirmation Dialog */}
        <Dialog
          open={generateConfirmOpen}
          onClose={() => setGenerateConfirmOpen(false)}
          maxWidth="sm"
        >
          <DialogTitle>
            🤖 Auto-Generate KPIs with AI?
          </DialogTitle>
          <DialogContent>
            <DialogContentText component="div">
              <Typography variant="body1" gutterBottom>
                This will automatically create 10 industry-specific KPIs based on your company's:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 2 }}>
                <li>Industry classification</li>
                <li>Company research data</li>
                <li>Business size and operations</li>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                💡 You can edit or delete any generated KPIs afterwards
              </Alert>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGenerateConfirmOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleGenerateKPIsConfirm}
              startIcon={<AutoAwesomeIcon />}
              color="primary"
            >
              Generate KPIs
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            KPI Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track your key performance indicators over time. Click any cell to enter data.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showAuditMarkers}
                onChange={handleToggleAuditMarkers}
                color="primary"
              />
            }
            label="Show Audit Markers"
          />
          <Button variant="outlined" startIcon={<SettingsIcon />} onClick={handleManageKPIs}>
            Manage KPIs
          </Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport}>
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* KPI Grid - HIDDEN - Using Landing Page Style Instead */}
      <Card sx={{ display: 'none' }}>
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 180, maxWidth: 180, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>KPI (Target)</TableCell>
                {gridData.periods.map((period: string) => (
                  <TableCell key={period} sx={{ fontWeight: 'bold', textAlign: 'center', minWidth: 80, maxWidth: 80, px: 0.5 }}>
                    {new Date(period).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {gridData.kpis.map((kpiData: any) => {
                const kpi = kpiData.kpi;
                return (
                  <TableRow key={kpi.id} hover>
                    <TableCell sx={{ minWidth: 180, maxWidth: 180, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        {showAuditMarkers && kpiData.has_audit_links && (
                          <Tooltip title={`Click to view all ${kpiData.audit_links?.length || 0} linked audit(s)`} arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleViewAuditDetails(kpi.id, kpi.name)}
                              sx={{ 
                                p: 0.25, 
                                mt: 0.25,
                                color: 'primary.main',
                                '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' }
                              }}
                            >
                              <AssignmentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.875rem' }}>
                            {kpi.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Target: {kpi.target_value} {kpi.unit_type}
                            {kpi.higher_is_better ? ' ↑' : ' ↓'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    {gridData.periods.map((period: string) => (
                      <EditableCell
                        key={period}
                        kpiId={kpi.id}
                        period={period}
                        entry={kpiData.entries[period]}
                        target={parseFloat(kpi.target_value)}
                        onSave={handleSaveEntry}
                        auditLinks={kpiData.audit_links || []}
                        showAuditMarkers={showAuditMarkers}
                        onViewAuditDetails={handleViewAuditDetails}
                        kpiName={kpi.name}
                      />
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Legend - HIDDEN */}
      <Box sx={{ mt: 2, display: 'none', gap: 3, justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#A5D6A7', borderRadius: 0.5 }} />
          <Typography variant="body2">Met/Exceeded Target</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#FFF9C4', borderRadius: 0.5 }} />
          <Typography variant="body2">Within Tolerance</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#EF9A9A', borderRadius: 0.5 }} />
          <Typography variant="body2">Below Target</Typography>
        </Box>
      </Box>

      {/* Landing Page Style Dashboard - Now Primary View */}
      <Box sx={{ mt: 2 }}>
        <LandingPageKpiDashboard 
          kpis={convertToLandingPageFormat(gridData)} 
          showAuditMarkers={showAuditMarkers}
          onViewAuditDetails={(kpiId, kpiName, period) => {
            handleViewAuditDetails(parseInt(kpiId), kpiName, period);
          }}
        />
      </Box>

      {/* KPI Create/Edit Dialog */}
      <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingKpi ? 'Edit KPI' : 'Create New KPI'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="KPI Name"
              value={kpiForm.name}
              onChange={(e) => setKpiForm({ ...kpiForm, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., On-Time Delivery Rate"
            />
            
            <TextField
              label="Description"
              value={kpiForm.description}
              onChange={(e) => setKpiForm({ ...kpiForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="What does this KPI measure?"
            />
            
            <FormControl fullWidth>
              <InputLabel>Unit Type</InputLabel>
              <Select
                value={kpiForm.unit_type}
                onChange={(e) => setKpiForm({ ...kpiForm, unit_type: e.target.value })}
                label="Unit Type"
              >
                <MenuItem value="percentage">Percentage (%)</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="currency">Currency ($)</MenuItem>
                <MenuItem value="days">Days</MenuItem>
                <MenuItem value="hours">Hours</MenuItem>
                <MenuItem value="count">Count</MenuItem>
                <MenuItem value="turns/year">Turns/Year</MenuItem>
                <MenuItem value="ratio">Ratio</MenuItem>
                <MenuItem value="score">Score</MenuItem>
                <MenuItem value="rate">Rate</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Target Value"
              value={kpiForm.target_value}
              onChange={(e) => setKpiForm({ ...kpiForm, target_value: e.target.value })}
              fullWidth
              required
              type="number"
              placeholder="e.g., 95"
            />
            
            <TextField
              label="Tolerance (%)"
              value={kpiForm.tolerance_percentage}
              onChange={(e) => setKpiForm({ ...kpiForm, tolerance_percentage: e.target.value })}
              fullWidth
              required
              type="number"
              placeholder="e.g., 10"
              helperText="Acceptable variance from target"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={kpiForm.higher_is_better}
                  onChange={(e) => setKpiForm({ ...kpiForm, higher_is_better: e.target.checked })}
                />
              }
              label="Higher is Better"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveKpi} 
            variant="contained" 
            disabled={!kpiForm.name || !kpiForm.target_value}
          >
            {editingKpi ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate KPIs Confirmation Dialog */}
      <Dialog
        open={generateConfirmOpen}
        onClose={() => setGenerateConfirmOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>
          🤖 Auto-Generate KPIs with AI?
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body1" gutterBottom>
              This will automatically create 10 industry-specific KPIs based on your company's:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 2 }}>
              <li>Industry classification</li>
              <li>Company research data</li>
              <li>Business size and operations</li>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              💡 You can edit or delete any generated KPIs afterwards
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateConfirmOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleGenerateKPIsConfirm}
            startIcon={<AutoAwesomeIcon />}
            color="primary"
          >
            Generate KPIs
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit Details Dialog */}
      <Dialog
        open={auditDetailsDialogOpen}
        onClose={() => setAuditDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6">Review Activity for KPI</Typography>
            {selectedKpiAudits && (
              <Typography variant="body2" color="text.secondary">
                {selectedKpiAudits.kpi.name}
                {selectedKpiAudits.period ? (
                  <> - {selectedKpiAudits.audits.length} audit(s) in {new Date(selectedKpiAudits.period).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</>
                ) : (
                  <> - {selectedKpiAudits.audits.length} audit(s) linked</>
                )}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingAuditDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : selectedKpiAudits && selectedKpiAudits.audits.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Review Name</strong></TableCell>
                    <TableCell><strong>Completed</strong></TableCell>
                    <TableCell align="center"><strong>Baseline</strong></TableCell>
                    <TableCell align="center"><strong>Latest</strong></TableCell>
                    <TableCell align="center"><strong>Change</strong></TableCell>
                    <TableCell align="center"><strong>Action Items</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedKpiAudits.audits.map((audit: any) => (
                    <TableRow key={audit.audit_id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {audit.audit_name}
                          </Typography>
                          <Chip 
                            label={audit.is_completed ? 'Completed' : 'In Progress'} 
                            color={audit.is_completed ? 'success' : 'warning'}
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(audit.audit_date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {audit.baseline_value ? (
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {audit.baseline_value} {selectedKpiAudits.kpi.unit_type}
                            </Typography>
                            {audit.baseline_date && (
                              <Typography variant="caption" color="text.secondary">
                                {new Date(audit.baseline_date).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {audit.latest_value ? (
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {audit.latest_value} {selectedKpiAudits.kpi.unit_type}
                            </Typography>
                            {audit.latest_value_date && (
                              <Typography variant="caption" color="text.secondary">
                                {new Date(audit.latest_value_date).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {audit.improvement ? (
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            color={parseFloat(audit.improvement) > 0 ? 'success.main' : 'error.main'}
                          >
                            {parseFloat(audit.improvement) > 0 ? '+' : ''}{audit.improvement}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {audit.completed_action_items || 0}/{audit.total_action_items || 0}
                        </Typography>
                        {audit.action_items && audit.action_items.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {audit.action_items.length} items
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No audit activity linked to this KPI yet.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditDetailsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage KPIs Dialog */}
      <Dialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Manage KPIs</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNewKPI}
            >
              Add New KPI
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {allKpis.length === 0 ? (
            <Alert severity="info">
              No KPIs configured yet. Click "Add New KPI" to create one.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Target</strong></TableCell>
                    <TableCell><strong>Unit</strong></TableCell>
                    <TableCell><strong>Tolerance</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allKpis.map((kpi) => (
                    <TableRow key={kpi.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {kpi.name}
                        </Typography>
                        {kpi.description && (
                          <Typography variant="caption" color="text.secondary">
                            {kpi.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {kpi.target_value}
                        {kpi.higher_is_better ? ' ↑' : ' ↓'}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={kpi.unit_type} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        ±{kpi.tolerance_percentage}%
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditKpi(kpi)}
                          title="Edit KPI"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteKpi(kpi.id, kpi.name)}
                          title="Delete KPI"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KPIDashboard;

